/**
 * Authentication return routing for installed games and the creation wizard.
 *
 * Games navigate to `/login` in the same PWA window. The initial same-origin
 * referrer identifies the game URL; sessionStorage carries it across Google's
 * external redirect; `/welcome` carries it through the one-time profile claim.
 * Only first-party standalone game paths are accepted, so this can never become
 * an open redirect.
 *
 * Both play shapes return: the floating `/play/<id>/` and a pinned
 * `/play/<id>/<n>/`. Signing in must never be the thing that strands a player
 * on the portal, and floating is now the address they launch from.
 */

export const GAME_AUTH_RETURN_KEY = 'djgg.auth.gameReturn';

export interface AuthReturnStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function safeGameReturnPath(value: string | null | undefined): string | null {
  if (!value || value.includes('\\') || /[\u0000-\u001f]/.test(value)) return null;
  try {
    const parsed = new URL(value, 'https://djgg.invalid');
    if (parsed.origin !== 'https://djgg.invalid') return null;
    if (parsed.pathname === '/studio' && parsed.search === '?new=1' && !parsed.hash) return '/studio?new=1';
    if (/^\/studio\/create(?:\/[a-zA-Z0-9_-]+)?$/.test(parsed.pathname) && !parsed.search && !parsed.hash) return parsed.pathname;
    if (!/^\/(?:games\/[^/]+|play\/[^/]+(?:\/\d+)?)(?:\/|$)/.test(parsed.pathname)) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function gameReturnFromReferrer(referrer: string, origin: string): string | null {
  try {
    const parsed = new URL(referrer);
    if (parsed.origin !== origin) return null;
    return safeGameReturnPath(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return null;
  }
}

/** Capture on first `/login` load; preserve it while Google owns the window. */
export function captureGameAuthReturn(
  referrer: string,
  origin: string,
  storage: AuthReturnStorage,
  requestedTarget?: string | null,
): string {
  const fromGame = safeGameReturnPath(requestedTarget) ?? gameReturnFromReferrer(referrer, origin);
  if (fromGame) {
    try {
      storage.setItem(GAME_AUTH_RETURN_KEY, fromGame);
    } catch {
      // The current navigation still carries the target for email auth; a
      // storage-denied browser simply cannot preserve it across Google.
    }
    return fromGame;
  }

  // A same-origin non-game referrer is a fresh ordinary portal login, so it
  // supersedes any abandoned game-login attempt left in this tab.
  try {
    if (new URL(referrer).origin === origin) {
      try {
        storage.removeItem(GAME_AUTH_RETURN_KEY);
      } catch {
        // A storage-denied browser has nothing reliable to clear.
      }
      return '/';
    }
  } catch {
    // Empty/cross-document referrer: Google may just have returned to us.
  }
  try {
    return safeGameReturnPath(storage.getItem(GAME_AUTH_RETURN_KEY)) ?? '/';
  } catch {
    return '/';
  }
}

export function welcomePathFor(returnTo: string): string {
  const game = safeGameReturnPath(returnTo);
  return game ? `/welcome?returnTo=${encodeURIComponent(game)}` : '/welcome';
}

export function gameReturnFromSearch(search: string): string {
  return safeGameReturnPath(new URLSearchParams(search).get('returnTo')) ?? '/';
}

export function clearGameAuthReturn(storage: AuthReturnStorage): void {
  try {
    storage.removeItem(GAME_AUTH_RETURN_KEY);
  } catch {
    // Auth itself succeeded; storage cleanup must never block navigation.
  }
}
